import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, KeyRound, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import Reveal, { MaskedLines } from "@/components/Reveal";
import { toast } from "sonner";

const MODES = {
  login: { title: "Tekrar hoş geldin", sub: "Hesabına giriş yap, ilan vermaya devam et." },
  register: { title: "Zonguldak'a katıl", sub: "30 saniyede üye ol, ilk ilanını ücretsiz ver." },
  forgot: { title: "Şifreni mı unuttun?", sub: "E-postanı gir, sıfırlama bağlantısı oluşturalım." },
};

export default function Auth({ mode = "login" }) {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [consents, setConsents] = useState({ kvkk: false, terms: false, cookies: false, marketing: false });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e.target }));
  const setConsent = (k) => (v) => setConsents((c) => ({ ...c, [k]: Boolean(v) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Giriş yapıldı. Hoş geldin!");
        navigate(loc.state?.from || "/", { replace: true });
      } else if (mode === "register") {
        await register({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
          consent_kvkk: consents.kvkk,
          consent_terms: consents.terms,
          consent_cookies: consents.cookies,
          consent_marketing: consents.marketing,
        });
        toast.success("Hesabın oluşturuldu. Hoş geldin!");
        navigate(loc.state?.from || "/ilan-ver", { replace: true });
      } else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setInfo(data.reset_link ? "Sıfırlama bağlantın hazırlandı." : "E-posta hesabını kontrol et.");
        if (data.reset_link) {
          window.__resetLink = data.reset_link;
        }
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const meta = MODES[mode];

  return (
    <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 pt-24 pb-20 lg:grid-cols-2 lg:items-center">
      <div className="hidden lg:block">
        <div className="relative overflow-hidden rounded-3xl bg-emerald2 text-bone grain p-10 min-h-[520px] flex flex-col justify-between">
          <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full bg-coral/25 blur-3xl" />
          <MaskedLines
            delay={0.2}
            lines={["Zonguldak'ın", <span key="c" className="text-coral">2. el pazarına</span>, "hoş geldin."]}
            className="font-display text-5xl font-black tracking-tight leading-[1.02]"
          />
          <div className="space-y-4">
            {[
              "Tek telefon numarası = tek hesap. Sahte profillere karşı koruma.",
              "İlanların yayın öncesi gerçek ekip tarafından onaylanır.",
              "Alıcıyla doğrudan WhatsApp'tan konuşursun, aracı yok.",
              "Verilerin KVKK kapsamında korunur, çerezler onayınla çalışır.",
            ].map((t, i) => (
              <Reveal key={i} delay={0.3 + i * 0.1}>
                <p className="flex items-start gap-3 text-sm text-bone/80">
                  <ShieldCheck size={17} className="mt-0.5 shrink-0 text-wa" /> {t}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto">
        <Reveal>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{meta.title}</h1>
          <p className="mt-2 text-slate-500">{meta.sub}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`auth-form-${mode}`}>
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Ad Soyad *</Label>
                <Input id="name" data-testid="register-name-input" required minLength={3} maxLength={40} value={form.name} onChange={set("name")} placeholder="Mehmet Yılmaz" />
                <p className="text-[11px] text-slate-400">Uygunsuz isimler otomatik olarak engellenir.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">E-posta *</Label>
              <Input id="email" data-testid="auth-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="ornek@posta.com" />
            </div>
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon (WhatsApp) *</Label>
                <Input id="phone" data-testid="register-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="0532 123 45 67" />
                <p className="text-[11px] text-slate-400">Bir telefon numarası ile yalnızca bir hesap açılabilir. Numaranız yalnızca alıcı-satıcı iletişimi için kullanılır.</p>
              </div>
            )}
            {mode !== "forgot" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Şifre *</Label>
                  {mode === "login" && (
                    <Link to="/sifremi-unuttum" data-testid="forgot-password-link" className="text-xs font-semibold text-coral hover:underline">
                      Şifremi unuttum
                    </Link>
                  )}
                </div>
                <Input id="password" data-testid="auth-password-input" required type="password" minLength={8} value={form.password} onChange={set("password")} placeholder="••••••••" />
                {mode === "register" && form.password && (() => {
                  const p = form.password; let s = 0;
                  if (p.length >= 8) s++;
                  if (p.length >= 12) s++;
                  if (/[0-9]/.test(p) && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(p)) s++;
                  if (/[^A-Za-z0-9]/.test(p)) s++;
                  const labels = ["Zayıf", "Zayıf", "Orta", "Güçlü"];
                  const colors = ["bg-red-500", "bg-red-400", "bg-amber2", "bg-emerald-500"];
                  return (
                    <div data-testid="password-strength-meter" className="pt-1">
                      <div className="flex gap-1.5">
                        {[0, 1, 2, 3].map((i) => (
                          <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < s ? colors[s] : "bg-slate-200"}`} />
                        ))}
                      </div>
                      <p className="mt-1 text-[11px] font-semibold text-slate-500">Parola gücü: {labels[s]} — en az 8 karakter, harf ve rakam içermelidir.</p>
                    </div>
                  );
                })()}
              </div>
            )}

            {mode === "register" && (
              <div className="space-y-3 rounded-2xl border border-black/5 bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Zorunlu Onaylar</p>
                {[
                  { k: "kvkk", id: "rkvkk", test: "kvkk-consent-checkbox", label: "KVKK Aydınlatma Metni", to: "/kvkk-aydinlatma-metni", text: "kapsamında kişisel verilerimin işlenmesini onaylıyorum. (Zorunlu)" },
                  { k: "terms", id: "rterms", test: "terms-consent-checkbox", label: "Üyelik Sözleşmesi & Kullanım Koşulları", to: "/kullanim-kosullari", text: "kurallarını okudum, kabul ediyorum. (Zorunlu)" },
                  { k: "cookies", id: "rcookies", test: "cookies-consent-checkbox", label: "Çerez Politikası", to: "/cerez-politikasi", text: "kapsamındaki çerez kullanımına izin veriyorum. (Zorunlu)" },
                ].map((c) => (
                  <div key={c.k} className="flex items-start gap-2.5">
                    <Checkbox data-testid={`${c.test}-register`} id={c.id} checked={consents[c.k]} onCheckedChange={setConsent(c.k)} className="mt-0.5" />
                    <label htmlFor={c.id} className="text-[12px] leading-relaxed text-slate-600">
                      <Link to={c.to} target="_blank" className="font-semibold text-coral underline underline-offset-2">{c.label}</Link>{" "}
                      {c.text}
                    </label>
                  </div>
                ))}
                <div className="flex items-start gap-2.5">
                  <Checkbox data-testid="marketing-consent-checkbox-register" id="rmarketing" checked={consents.marketing} onCheckedChange={setConsent("marketing")} className="mt-0.5" />
                  <label htmlFor="rmarketing" className="text-[12px] leading-relaxed text-slate-600">
                    Kampanya ve duyurulardan e-posta ile haberdar olmak istiyorum. <span className="text-slate-400">(İsteğe bağlı)</span>
                  </label>
                </div>
              </div>
            )}

            {error && (
              <p data-testid="auth-error-message" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            <Button
              data-testid={mode === "login" ? "login-submit-button" : mode === "register" ? "register-submit-button" : "forgot-submit-button"}
              type="submit" disabled={busy}
              className="w-full rounded-full bg-coral py-3.5 text-sm font-bold text-white hover:bg-coraldark transition shadow-lg shadow-coral/25"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : mode === "login" ? "Giriş Yap" : mode === "register" ? "Hesabımı Oluştur" : "Sıfırlama Bağlantısı Oluştur"}
            </Button>

            {mode === "login" && (
              <p className="text-center text-sm text-slate-500">
                Hesabın yok mu? <Link to="/kayit" data-testid="go-register-link" className="font-bold text-coral hover:underline">Ücretsiz üye ol</Link>
              </p>
            )}
            {mode === "register" && (
              <p className="text-center text-sm text-slate-500">
                Zaten üye misin? <Link to="/giris" data-testid="go-login-link" className="font-bold text-coral hover:underline">Giriş yap</Link>
              </p>
            )}

            {mode === "forgot" && info && (
              <div data-testid="forgot-success-box" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <p className="flex items-center gap-2 font-semibold"><MailCheck size={15} /> {info}</p>
                {window.__resetLink && (
                  <p className="mt-2 break-all text-xs leading-relaxed">
                    E-posta servisi entegre edilene kadar bağlantınız burada:{" "}
                    <a href={window.__resetLink} data-testid="reset-link-anchor" className="font-bold underline">Şifremi sıfırla</a>
                  </p>
                )}
              </div>
            )}
          </form>

          {mode === "forgot" && (
            <p className="mt-6 text-center text-sm text-slate-500 inline-flex items-center gap-1.5">
              <KeyRound size={14} /> <Link to="/giris" data-testid="back-login-link" className="font-bold text-coral hover:underline">Girişe dön</Link>
            </p>
          )}
        </Reveal>
      </div>
    </div>
  );
}
